"use client";
import mixpanel from "mixpanel-browser";
import constate from "constate";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Contact } from "@/app/(hub)/contacts/types";
import { Product, Products, Variant } from "@/app/(hub)/products/types";
import { createPurchaseOrder } from "@/app/(hub)/purchase-orders/services";
import { PurchaseOrderStatus } from "@/app/(hub)/purchase-orders/types";
import { toast } from "react-toastify";
import { useStoreContext } from "./provider";
import dayjs from "dayjs";
import { getErrorMessage } from "../utils";

interface Cart {
  product: Product;
  variant: Variant;
  quantity: number;
}

export function useStorefrontStore() {
  const router = useRouter();
  const { outlet } = useStoreContext();
  const searchParams = useSearchParams();
  const contactId = searchParams.get("contact");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sorted, setSorted] = useState<Products | undefined>([]);
  const [products, setProducts] = useState<Products>([]);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<
    Products | undefined
  >([]);
  const [contact, setContact] = useState<Contact | undefined>();
  const [cart, setCart] = useState<Cart[] | undefined>([]);
  const [canOrder, setCanOrder] = useState(false);
  const sortOptions = [
    { label: "Default", value: "default" },
    { label: "Price: Low to High", value: "lowest" },
    { label: "Price: High to Low", value: "highest" },
  ];

  useEffect(() => {
    if (contactId) {
      const existing = localStorage.getItem(`cart-${contactId}`);
      if (existing) {
        setCart(JSON.parse(existing));
      }
    } else {
      const existing = localStorage.getItem("cart");
      if (existing) {
        setCart(JSON.parse(existing));
      }
    }
  }, [contactId]);

  useEffect(() => {
    if (contactId) {
      localStorage.setItem(`cart-${contactId}`, JSON.stringify(cart));
    } else {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart]);

  useEffect(() => {
    const filtered = products.filter(
      (product) =>
        product?.category &&
        filteredCategories.includes(product.category[0]._id),
    );
    if (!filteredCategories.length) {
      setFilteredProducts(products);
    } else setFilteredProducts(filtered);
  }, [filteredCategories]);

  useEffect(() => {
    setFilteredProducts(sorted);
  }, [sorted]);

  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  function onFilter(id: string, checked: boolean) {
    setFilteredCategories((prev) => {
      if (checked) return [...prev, id];
      return prev.filter((category) => category !== id);
    });
  }

  function onSort(sort: string) {
    let sortedProducts: Products;
    if (sort === "lowest") {
      sortedProducts = [...products].sort(
        (a, b) => Number(a.variants[0].price) - Number(b.variants[0].price),
      );
    } else if (sort === "highest") {
      sortedProducts = [...products].sort(
        (a, b) => Number(b.variants[0].price) - Number(a.variants[0].price),
      );
    } else {
      sortedProducts = products;
    }
    setSorted(sortedProducts);
  }

  function addToCart(product: Product, variant: Variant, quantity: number) {
    setCart((prev) => {
      if (prev) {
        const existingItem = prev.find(
          (item) =>
            item.product._id === product._id &&
            item.variant._id === variant._id,
        );
        if (existingItem) {
          toast.success("Cart updated!");
          existingItem.quantity = quantity;
          return [...prev];
        } else {
          toast.success("Added to cart!");
          return [...prev, { product, variant, quantity }];
        }
      } else {
        toast.success("Added to cart!");
        return [{ product, variant, quantity }];
      }
    });
  }

  function removeFromCart(variant: Variant) {
    setCart((prev) => {
      if (prev) {
        const updatedCart = prev.filter(
          (item) => item.variant._id !== variant._id,
        );
        return updatedCart;
      } else return [];
    });
    toast.success("Removed from cart!");
  }

  async function createDraft() {
    if (!contact || !cart) return;
    setLoading(true);

    const res = await createPurchaseOrder({
      status: PurchaseOrderStatus.DRAFT,
      contactId: contact.id,
      outletId: outlet,
      orderDate: new Date().toISOString(),
      deliveryDate: dayjs().add(14, "day").toISOString(),
      deliveryAddress: "",
      netTerms: 14,
      products: cart?.map((item) => {
        return {
          name: item.product.name,
          productId: item.product.id,
          variant: {
            id: item.variant._id,
            name: item.variant.option
              ? `${Object.values(item.variant?.option ?? {}).join("/")}`
              : "No variant",
            price: item.variant.price,
          },
          quantity: item.quantity,
          unitPrice: item.variant.price,
        };
      }),
    });

    if (!res.error) {
      toast.success("Draft created successfully!");
      if (contactId) {
        localStorage.removeItem(`cart-${contactId}`);
      } else {
        localStorage.removeItem("cart");
      }
      setCart([]);
      setLoading(false);
      router.push("/purchase-orders/drafts");
    } else {
      toast.error(getErrorMessage(res));
      setLoading(false);
    }
  }

  useEffect(() => {
    mixpanel.track("Storefront", {
      Action: "Viewed",
    });
  }, []);

  return {
    open,
    setOpen,
    sortOptions,
    setProducts,
    filteredProducts,
    setFilteredProducts,
    onSort,
    onFilter,
    cart,
    setCart,
    addToCart,
    removeFromCart,
    createDraft,
    canOrder,
    setCanOrder,
    setContact,
    contact,
    loading,
  };
}

export const [StorefrontProvider, useStorefrontContext] =
  constate(useStorefrontStore);
